import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader, Card, ui } from "@/components/Primitives";
import { BRANDING } from "@/config/branding";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Inbox, User, CreditCard, FlaskConical, Check, LayoutGrid } from "lucide-react";

const DEV_TIERS = [
  { key: "personal", label: "Personal" },
  { key: "investor", label: "Investor" },
  { key: "community", label: "Community" },
  { key: "all_access", label: "All Access" },
];

const WS_NAME = { personal: "Personal Finance", portfolio: "Portfolio", community: "Community" };

export default function Settings() {
  const { user, entitlements, setCurrency, setDevTier, setWorkspaces } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [ws, setWs] = useState(null);
  const [wsBusy, setWsBusy] = useState("");
  const tiers = entitlements?.tiers || {};

  useEffect(() => {
    api.get("/workspaces").then((r) => setWs(r.data)).catch(() => {});
  }, []);

  const toggleWorkspace = async (key, on) => {
    if (!ws) return;
    setWsBusy(key);
    const next = new Set(ws.enabled);
    if (on) next.add(key); else next.delete(key);
    try {
      const data = await setWorkspaces(Array.from(next));
      setWs((w) => ({ ...w, enabled: data.enabled, available: data.available }));
      toast.success(`${WS_NAME[key] || key} ${on ? "enabled" : "disabled"}`);
    } catch { toast.error("Could not update workspace"); }
    finally { setWsBusy(""); }
  };

  const copyInbox = () => {
    navigator.clipboard?.writeText(BRANDING.inboxEmail || "");
    toast.success("Copied to clipboard");
  };
  const changeCurrency = async (c) => { await setCurrency(c); toast.success("Currency updated"); };
  const switchTier = async (t) => {
    setSwitching(true);
    try { await setDevTier(t); toast.success(`Switched to ${t === "all_access" ? "All Access" : t}`); }
    catch { toast.error("Could not switch tier"); }
    finally { setSwitching(false); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage your home, your plan and your preferences." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card data-testid="settings-profile">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center"><User size={18} className="text-[#C6A45C]" /></div>
            <h3 className="font-display text-xl text-[#2C2A28]">Your Profile</h3>
          </div>
          <div className="space-y-3 text-sm">
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={user?.email} />
            <Row label="Country" value={user?.country} />
            <Row label="Household" value={user?.household} />
            <div className="pt-2">
              <Label className={ui.label}>Currency</Label>
              <Select value={user?.currency || "AUD"} onValueChange={changeCurrency}>
                <SelectTrigger data-testid="settings-currency" className={ui.selectTrigger}><SelectValue /></SelectTrigger>
                <SelectContent className={ui.selectContent}>
                  <SelectItem value="AUD">AUD — A$</SelectItem>
                  <SelectItem value="NZD">NZD — NZ$</SelectItem>
                  <SelectItem value="USD">USD — $</SelectItem>
                  <SelectItem value="EUR">EUR — €</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card data-testid="settings-inbox">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center"><Inbox size={18} className="text-[#C6A45C]" /></div>
            <h3 className="font-display text-xl text-[#2C2A28]">Financial Inbox</h3>
          </div>
          <p className="text-sm text-[#8A7F70] mb-3">Email your receipts &amp; statements here — send from your account email and they appear in your Inbox automatically.</p>
          <div className="flex items-center gap-2 bg-white/70 border border-[#EAE0D2] rounded-xl px-4 py-3">
            <span data-testid="settings-inbox-email" className="font-mono-data text-sm gold-text truncate flex-1">{BRANDING.inboxEmail || "—"}</span>
            <button data-testid="copy-inbox-btn" onClick={copyInbox} className="text-[#8A7F70] hover:text-[#C6A45C]"><Copy size={16} /></button>
          </div>
        </Card>

        <Card className="lg:col-span-2" data-testid="settings-plan">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center"><CreditCard size={18} className="text-[#C6A45C]" /></div>
            <h3 className="font-display text-xl text-[#2C2A28]">Your Plan</h3>
            <Badge className="ml-auto bg-[#C6A45C]/15 text-[#8a6d2f] border-[#C6A45C]/30">{entitlements?.tier_name}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(tiers).map(([key, t]) => {
              const current = entitlements?.tier === key;
              return (
                <div key={key} className={`rounded-2xl p-5 border ${current ? "border-[#C6A45C] bg-[#C6A45C]/5" : "border-[#EAE0D2] bg-white/50"}`}>
                  <div className="font-display text-lg text-[#2C2A28]">{t.name}</div>
                  <div className="font-display text-3xl font-light mt-2 text-[#2C2A28]">${t.price}<span className="text-sm text-[#A99F90]">/mo</span></div>
                  {current && <div className="text-xs gold-text mt-3 flex items-center gap-1"><Check size={13} /> Current plan</div>}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#A99F90] mt-4">Secure billing & plan upgrades arrive in a later {BRANDING.name} release (V2.5).</p>
        </Card>

        {ws && (
          <Card className="lg:col-span-2" data-testid="settings-workspaces">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center"><LayoutGrid size={18} className="text-[#C6A45C]" /></div>
              <h3 className="font-display text-xl text-[#2C2A28]">Workspaces</h3>
              {ws.beta_access && <Badge className="ml-auto bg-[#8FB996]/20 text-[#5a9a6a] border-[#8FB996]/40">Beta Access</Badge>}
            </div>
            <p className="text-sm text-[#8A7F70] mb-5">Enable the workspaces you want to use. During beta you can switch freely — your navigation updates instantly, no new account required.</p>
            <div className="space-y-3">
              {ws.available.map((w) => (
                <div key={w.key} data-testid={`workspace-row-${w.key}`} className="flex items-center justify-between gap-4 bg-white/60 border border-[#EAE0D2] rounded-xl px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[#2C2A28] font-medium text-sm">{w.name}</span>
                      {w.locked && <Badge className="bg-[#EAE0D2] text-[#8A7F70] border-0 text-[10px]">Always on</Badge>}
                    </div>
                    <div className="text-xs text-[#A99F90] mt-0.5">{w.description}</div>
                  </div>
                  <Switch data-testid={`workspace-toggle-${w.key}`} checked={w.enabled} disabled={w.locked || wsBusy === w.key}
                    onCheckedChange={(v) => toggleWorkspace(w.key, v)} />
                </div>
              ))}
            </div>
            <p className="text-xs text-[#A99F90] mt-4">Workspace access will be governed by your subscription once secure billing launches.</p>
          </Card>
        )}

        {user?.role === "admin" && (
          <Card className="lg:col-span-2 border-[#C6A45C]/30" data-testid="settings-devmode">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center"><FlaskConical size={18} className="text-[#C6A45C]" /></div>
              <h3 className="font-display text-xl text-[#2C2A28]">Developer Mode</h3>
              <Badge className="bg-[#9FB8C9]/20 text-[#5a7a8c] border-[#9FB8C9]/30">Admin only</Badge>
            </div>
            <p className="text-sm text-[#8A7F70] mb-4">Instantly switch your account tier to test module access — no Stripe required.</p>
            <div className="flex flex-wrap gap-2">
              {DEV_TIERS.map((t) => {
                const active = entitlements?.tier === t.key;
                return (
                  <button key={t.key} data-testid={`devtier-${t.key}`} disabled={switching} onClick={() => switchTier(t.key)}
                    className={`px-4 py-2.5 rounded-full text-sm transition-all border ${
                      active ? "bg-[#C6A45C] text-white border-[#C6A45C]" : "bg-white/60 text-[#6b6256] border-[#EAE0D2] hover:border-[#C6A45C]"}`}>
                    {t.label}{active && " ✓"}
                  </button>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-[#F0E9DD] pb-2">
    <span className="text-[#8A7F70]">{label}</span>
    <span className="text-[#2C2A28]">{value || "—"}</span>
  </div>
);
